'use client'

import { Slot } from '@radix-ui/react-slot'
import { cx } from 'class-variance-authority';
import { useInView } from 'motion/react'
import {
  Children,
  type ComponentProps,
  useImperativeHandle,
  useRef,
  type ComponentPropsWithRef, forwardRef, type ElementType
} from 'react';

interface TextSlideUpItemProps extends ComponentProps<'div'> {
  delay?: number
  animate?: boolean
}

function TextSlideUpItem({
                           children,
                           delay,
                           animate,
                           className,
                           ...props
                         }: TextSlideUpItemProps) {
  return (
    <div className={className} {...props}>
      <div
        className={cx({
          'motion-preset-fade motion-translate-x-in-[0%] motion-translate-y-in-[100%] [animation-duration:800ms]':
          animate,
          invisible: !animate,
        })}
        style={{ animationDelay: delay ? `${delay}ms` : undefined }}
      >
        {children}
      </div>
    </div>
  )
}

export interface TextSlideUpProps extends ComponentProps<'div'> {
  staggerDelay?: number
  delay?: number
  asChild?: boolean
  eager?: boolean
}

const AnimatedBubbleText = forwardRef<
  HTMLSpanElement,
  Omit<ComponentPropsWithRef<'span'>, 'children'> & {
  as?: ElementType
  value?: string
  letterClassName?: string
}
>(function AnimatedBubbleText({ as, value, letterClassName, ...props }, ref) {
  const Tag = as ?? 'span'

  const letters = value?.split('')

  return (
    // @ts-ignore
    <Tag {...props} aria-label={value} ref={ref}>
      {letters?.map((letter, index) => (
        <span
          className={cx(
            'inline-block transition-colors',
            'hover:animate-rubber cursor-default',
            letterClassName,
          )}
          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
          key={`${letter}-${index}`}
        >
					{letter === ' ' ? '\u00A0' : letter}
				</span>
      ))}
    </Tag>
  )
})


export function TextSlideUp({
                              children,
                              staggerDelay = 200,
                              delay = 0,
                              asChild = false,
                              eager,
                              className,
                              ref,
                              ...props
                            }: TextSlideUpProps) {
  const componentRef = useRef<HTMLDivElement>(null)
  useImperativeHandle(ref, () => componentRef.current as HTMLDivElement)

  const Component = asChild ? Slot : 'div'
  const inView = useInView(componentRef, { once: true })
  const animate = eager ?? inView

  return (
    <Component ref={componentRef} className={className} {...props}>
      {Children.map(children, (child, index) => {
        const stagger = staggerDelay ? index * staggerDelay : 0
        const itemDelay = delay + stagger

        return (
          <TextSlideUpItem
            className={className}
            animate={animate}
            delay={itemDelay}
          >
            {child}
          </TextSlideUpItem>
        )
      })}
    </Component>
  )
}
